import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { Subscription } from '../components/subscription/SubscriptionStatus';
import { Transaction } from '../components/subscription/BillingHistory';
import { useAuth } from './AuthContext';
import {
  getCurrentSubscription,
  getBillingHistory,
  cancelSubscription as cancelSubscriptionService,
  createSubscription,
  startFreeTrial
} from '../services/stripeService';

// Define types
interface SubscriptionContextType {
  subscription: Subscription | null;
  transactions: Transaction[];
  isLoading: boolean;
  error: string | null;
  cancelSubscription: (cancelImmediately?: boolean) => Promise<void>;
  createNewSubscription: (planId: string, paymentMethodId: string) => Promise<void>;
  startTrial: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  refreshTransactions: () => Promise<void>;
}

// Create the context
const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// Provider component
export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch subscription data when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      refreshSubscription();
      refreshTransactions();
    } else {
      // Reset state when user logs out
      setSubscription(null);
      setTransactions([]);
    }
  }, [isAuthenticated, user]);

  // Refresh subscription data
  const refreshSubscription = async () => {
    if (!isAuthenticated || !user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const subscriptionData = await getCurrentSubscription(user.username);
      setSubscription(subscriptionData);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch subscription data');
      console.error('Error fetching subscription:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh transaction history
  const refreshTransactions = async () => {
    if (!isAuthenticated || !user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const transactionData = await getBillingHistory(user.username);
      setTransactions(transactionData);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch transaction history');
      console.error('Error fetching transactions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel subscription
  const cancelSubscription = async (cancelImmediately: boolean = false) => {
    if (!subscription) {
      setError('No active subscription to cancel');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const updatedSubscription = await cancelSubscriptionService(subscription.id, cancelImmediately);
      setSubscription(updatedSubscription);
    } catch (err: any) {
      // If subscription not found (404), treat as if it's already canceled
      if (err.response && err.response.status === 404) {
        // Set subscription to null since it doesn't exist in Stripe
        setSubscription(null);
        // Refresh subscription data
        refreshSubscription();
        return;
      }
      
      setError(err.message || 'Failed to cancel subscription');
      console.error('Error canceling subscription:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Create new subscription
  const createNewSubscription = async (planId: string, paymentMethodId: string) => {
    if (!isAuthenticated || !user) {
      setError('User must be authenticated to create a subscription');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const newSubscription = await createSubscription(user.username, planId, paymentMethodId);
      setSubscription(newSubscription);
      await refreshTransactions();
    } catch (err: any) {
      setError(err.message || 'Failed to create subscription');
      console.error('Error creating subscription:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Start free trial
  const startTrial = async () => {
    if (!isAuthenticated || !user) {
      setError('User must be authenticated to start a trial');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const trialSubscription = await startFreeTrial(user.username, user.email);
      setSubscription(trialSubscription);
    } catch (err: any) {
      setError(err.message || 'Failed to start trial');
      console.error('Error starting trial:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    subscription,
    transactions,
    isLoading,
    error,
    cancelSubscription,
    createNewSubscription,
    startTrial,
    refreshSubscription,
    refreshTransactions
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

// Custom hook to use the subscription context
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
